import { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router';
import { colors } from '@tempmee/tailwind';

import { remoteConfigReady } from '~/api/firebaseConfig';
import { SaltclickChatbot } from '~/constants/ff.posthog';
import {
  buildSaltclickChatIdentity,
  clearSaltclickSession,
  closeOpenSaltclickConversations,
  endSaltclickChatSession,
  fetchSaltclickIdentityToken,
  getSaltclickVerifiedForUserId,
  getStoredSaltclickConversationIds,
  installSaltclickEndSignals,
  listOpenSaltclickConversationIds,
  purgeStoredSaltclickConversation,
  ISaltclickChatIdentity,
  ISaltclickSessionStatusPayload,
  setSaltclickVerifiedForUserId,
  toSaltclickPrechatFields,
} from '~/domain/SaltclickChat';
import { useFeatureFlag } from '~/hooks/useFeatureFlag';
import { fireHelpChatbotButtonTapped } from '~/posthogs/events';
import { routes } from '~/routes';
import { TRootState } from '~/store/types';
import { DevLogger } from '~/utils/DevLogger';
import { RemoteConfigUtils } from '~/utils/RemoteConfigUtils';

const SCRIPT_ID = 'saltclick-bootstrap';
// Salesforce can fail silently (blocked script, CORS on its own subrequests),
// so the skeleton gets a deadline instead of pulsing forever.
const SKELETON_TIMEOUT_MS = 20000;
/**
 * How long after the ready event to check that a launcher actually painted. Ready
 * fires before the widget renders, so an immediate check would always fail.
 */
const PAINT_CHECK_DELAY_MS = 5000;
const STYLE_ID = 'saltclick-visibility';
const VISIBLE_CLASS = 'saltclick-visible';
const READY_CLASS = 'saltclick-ready';
/** Set while a conversation is being retired — see `RETIRING_UNBLOCK_MS`. */
const RETIRING_CLASS = 'saltclick-retiring';
/**
 * Set while the widget is showing a conversation that has been retired — see
 * `STALE_VIEW_GUARD_MS`.
 */
const GUARDED_CLASS = 'saltclick-guarded';
/**
 * How long the widget may be inert while it replaces a retired conversation.
 *
 * The window this covers: the first time the chat window is opened after a chat was
 * retired, it comes back showing the view the ended conversation left behind — including
 * a **New Conversation** button, which does what it always does and starts a new *session*
 * on the conversation the widget is still holding. That conversation was deleted, so the
 * button reconnects forever and takes the widget's UI with it, unrecoverable without a
 * reload. It cannot be disabled from here: it is rendered on Salesforce's origin, so the
 * only reach this page has is the frame it sits in.
 *
 * The widget opens a fresh conversation on its own within a second or two, and that is
 * what replaces the stale view — so this is the gap between opening and that arriving,
 * not a feature. A deadline rather than a promise, for the same reason as
 * `RETIRING_UNBLOCK_MS`: a widget nobody hands back is worse than a button nobody should
 * press.
 */
const STALE_VIEW_GUARD_MS = 8000;
/**
 * How long an ended chat is left resumable before its conversation is retired.
 *
 * An end — the user's End chat, a bot timing the session out, an agent closing from their
 * side — ends the messaging *session* and leaves the conversation open. Typing into it
 * starts a new session on the same conversation with its history intact, and that is the
 * window this holds open: reply inside it and nothing is taken away; walk away and the
 * conversation is closed so the next chat starts clean.
 *
 * The cost, agreed with support (2026-09-17): a session resumed this way carries no
 * `gtAdminPanelId`. Salesforce takes hidden pre-chat fields only before a conversation
 * begins, and a resumed session begins none — its own docs say pre-chat data "isn't
 * carried over" to the new session, and the only remedy they offer is on the Omni-Channel
 * flow side. Support would rather keep the history and monitor how often the office is
 * missing.
 */
const REPLY_WINDOW_MS = 2 * 60 * 1000;
/**
 * How long the launcher may stay a placeholder before the widget is handed back
 * regardless.
 *
 * Retiring a conversation is several round trips to two hosts, and a launcher that never
 * comes back is worse than any of the churn this replaces — `hideChatButton` was dropped
 * from this flow for exactly that reason. So it is a deadline, not a promise about the
 * flow finishing, and it is why hiding the widget here is safe when hiding it by API call
 * was not.
 */
const RETIRING_UNBLOCK_MS = 8000;

/**
 * Salesforce Embedded Messaging renders everything (button + chat) into a
 * `#embedded-messaging` container and can only be init'd once per page load,
 * so visibility is driven by a body class instead of unloading the widget.
 * The widget renders async after init; the CSS rule keeps it hidden on any
 * route where the Help page isn't mounted, even if init finishes after the
 * user has navigated away.
 *
 * The `::after` skeleton stands in for the launcher whenever there is no launcher
 * worth clicking — before the widget paints, and again while a conversation is
 * being retired. Both are the same state from where the user sits: something is
 * happening and the corner is not ready yet, so it says so in the launcher's own
 * place and size rather than leaving a button that ignores clicks. ponytail: a
 * pseudo-element keeps this out of the React tree; promote it to a real component
 * if it ever needs interaction.
 */
const ensureVisibilityStyle = (): void => {
  if (document.getElementById(STYLE_ID)) return;
  const style = document.createElement('style');
  style.id = STYLE_ID;
  style.textContent = `
    /* Out of the way while its conversation is retired, and the placeholder below takes
       its place. Not polish: the flow lists every open conversation and closes what it
       finds, so a user who reopens the launcher and starts a new chat mid-flight would
       have that new conversation closed under them. It also spares them the churn —
       reopening a window that \`clearSession\` is about to tear down and rebuild is what
       makes the widget blink repeatedly. \`RETIRING_CLASS\` is removed in a \`finally\`
       and on a deadline, so neither the hiding nor the placeholder can outlast the flow. */
    body:not(.${VISIBLE_CLASS}) #embedded-messaging,
    body.${RETIRING_CLASS} #embedded-messaging { display: none !important; }
    /* Belt and braces on the same thing, for a deployment whose own styles win the
       display fight: a widget that is somehow still painted still takes no clicks. */
    body.${RETIRING_CLASS} #embedded-messaging { pointer-events: none !important; }
    /* Visible but inert, which is the difference from the rule above. The widget is
       showing a retired conversation's leftover view and is about to replace it with a
       live one; until it does, its **New Conversation** button would start a session on
       the conversation we deleted. The button cannot be reached from this origin, so the
       frame it sits in is what goes inert — the user sees the widget connecting, which is
       what is actually happening, and cannot press the one thing that breaks it. */
    body.${GUARDED_CLASS} #embedded-messaging { pointer-events: none !important; }
    body.${VISIBLE_CLASS}:not(.${READY_CLASS})::after,
    body.${VISIBLE_CLASS}.${RETIRING_CLASS}::after {
      content: '';
      position: fixed;
      bottom: 1.25rem;
      right: 1.25rem;
      width: 3.75rem;
      height: 3.75rem;
      border-radius: 9999px;
      background: ${colors.neutral[200]};
      animation: saltclick-skeleton-pulse 1.5s ease-in-out infinite;
    }
    @keyframes saltclick-skeleton-pulse { 50% { opacity: 0.4; } }
    @media (prefers-reduced-motion: reduce) {
      body.${VISIBLE_CLASS}:not(.${READY_CLASS})::after,
      body.${VISIBLE_CLASS}.${RETIRING_CLASS}::after { animation: none; }
    }
  `;
  document.head.appendChild(style);
};

/**
 * Puts an open chat window away.
 *
 * Called before anything that resets the widget, because a window left open is what makes
 * it relaunch itself afterwards, and that relaunch is the blink a user sees. Probed rather
 * than assumed — this deployment runs the WebV2 client, whose surface is not the
 * documented one — and reported when absent, since a missing member turns the churn this
 * prevents back on with nothing saying why.
 */
const minimizeChatWindow = (): void => {
  const utilApi = window.embeddedservice_bootstrap?.utilAPI;

  try {
    if (utilApi?.minimizeChat) utilApi.minimizeChat();
    else
      DevLogger.error(
        '[Saltclick] No utilAPI.minimizeChat on this deployment — an open chat window will ' +
          'be torn down in place',
      );
  } catch (err) {
    DevLogger.error('[Saltclick] minimizeChat threw: ', err);
  }
};

/**
 * Embeds the Saltclick (Salesforce Embedded Messaging) chatbot. Called once
 * from the authenticated layout so the widget boots in the background right
 * after login and the launcher is already painted (just hidden) by the time
 * the user opens Help — no load-then-pop. Gated by the
 * `GOTU-4448-saltclick-chatbot` PostHog flag. GOTU-5243.
 *
 * The session is verified rather than anonymous: auth-service mints a short-lived
 * JWT for the logged-in account and the widget is told to trust it, which is what
 * separates one user's conversation from the next in a shared tab. GOTU-5918.
 *
 * And a chat the user ends stays ended: Salesforce's own End chat ends the messaging
 * session but leaves the conversation open, and the widget resumes any open conversation
 * as it boots — so the next chat continued the last one. `handleChatEnded` retires it
 * over the MIAW REST API, which is the one thing here no client API can do.
 */
export const useSaltclickChat = (): void => {
  const flagEnabled = useFeatureFlag(SaltclickChatbot);
  /*
   * The account that logged in, and the only identity this feature has.
   *
   * `useUserSelector` would answer with the office being acted as, and pre-chat used to
   * report that — who the chat is about. It no longer does, because it cannot be both:
   * auth-service mints the identity JWT from the access token, which belongs to the
   * account and is not re-issued by an office switch, so the two ids disagree for a
   * parent acting as a child and `gtAdminPanelId` then names an office the verified
   * session is not for. Salesforce keys session control off that field, so disagreeing
   * with the token it verified is the worse of the two answers.
   *
   * The cost is that a parent acting as a child office reaches support as the parent —
   * which is the account support can act for. Mirrored on mobile, which had the same
   * split and the same conclusion.
   */
  const user = useSelector((state: TRootState) => state.user.user);
  const accountId = user?.id;
  // Logged-in users only — never show the widget when Help is reached from
  // login/sign-up (GOTU-5243).
  const enabled = flagEnabled && !!user;
  const isHelpRoute = useLocation().pathname === routes.help;

  const [isMessagingReady, setIsMessagingReady] = useState(false);

  // Read from event handlers (ready, token expiry) that outlive the render they
  // were registered in, so they need the current user, not a captured one.
  const identityRef = useRef<ISaltclickChatIdentity | null>(null);
  const accountIdRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    identityRef.current = buildSaltclickChatIdentity(user);
    accountIdRef.current = accountId;
  }, [accountId, user]);

  /**
   * Stages the hidden pre-chat fields for the NEXT conversation.
   *
   * Salesforce takes these only after the ready event and before a conversation
   * begins, and a late write is dropped in silence — no error, no clue. So every
   * conversation needs its own, which is why this is callable rather than only an
   * effect: `verifySession` follows every `clearSession`, and the rebuilt widget has
   * nothing staged until this runs again.
   *
   * Those two windows, and no third. Writes on the launcher click and on a session
   * going `Active` were tried and attached to nothing: a session resumed on an open
   * conversation begins no conversation, so there is no moment for them to land in
   * (measured on dev, 2026-09-14 — see `REPLY_WINDOW_MS` for the trade this leaves).
   *
   * `gtAdminPanelId` is the field Salesforce keys session control off; `userType`
   * tells an agent which kind of account they are talking to.
   */
  const sendPrechatFields = useCallback((): void => {
    const identity = identityRef.current;
    if (!identity) return;

    const prechatApi = window.embeddedservice_bootstrap?.prechatAPI;
    if (!prechatApi) {
      // Before ready the widget simply does not exist yet, and `isMessagingReady`
      // brings the effect below back — so only a *ready* widget without `prechatAPI`
      // is an anomaly. Reported for the same reason as the missing
      // `userVerificationAPI` above: the conversation would otherwise reach an agent
      // with no office on it and nothing would say why.
      if (window.embeddedservice_bootstrap) {
        DevLogger.error(
          '[Saltclick] Widget is ready but exposes no prechatAPI — no pre-chat ' +
            'form is configured on this Salesforce deployment, so the conversation ' +
            'carries no gtAdminPanelId',
        );
      }

      return;
    }

    const fields = toSaltclickPrechatFields(identity);
    prechatApi.setHiddenPrechatFields(fields);
    // Field *names* only: the identity holds PII (`email`) that this must never
    // put in a console. Which keys went is the useful part anyway — Salesforce
    // discards undeclared fields silently, so `DECLARED_PRECHAT_FIELDS` is what
    // this line lets you confirm against their config.
    DevLogger.log(
      `[Saltclick] Sent hidden pre-chat fields for ${identity.gtAdminPanelId}: ` +
        `${Object.keys(fields).join(', ')}`,
    );
  }, []);

  /** The account a token request is currently out for, if any. */
  const requestedForRef = useRef<string | null>(null);
  /**
   * Fetches a token for whoever is logged in now and hands it to the widget.
   *
   * Every trigger — first ready, a fresh login, token expiry, a closed
   * conversation — re-calls the endpoint rather than reusing a held token. A token
   * is short-lived (900s) and bound to the conversation it opened, so a cached one
   * is the wrong thing to re-hand; and keeping none means each trigger is
   * self-contained, instead of the outcome depending on which of two paths reached
   * the shared token first.
   *
   * The marker is the only thing that makes this cheap to call: already verified as
   * this account means there is nothing to do, so the repeat triggers cost nothing.
   */
  const verifySession = useCallback(async (): Promise<void> => {
    const account = accountIdRef.current;
    if (!account) return;

    // Nothing to hand a token to yet. `userVerificationAPI` appears with the
    // widget, and the ready event re-runs this — so this is the normal early path,
    // not a failure (`onReady` reports a ready widget that still lacks it).
    const verificationApi = window.embeddedservice_bootstrap?.userVerificationAPI;
    if (!verificationApi) return;

    if (getSaltclickVerifiedForUserId() === account) return;

    // One request in flight per account: this runs from the mount, from the ready
    // event, from token expiry and from a closed conversation, which overlap.
    if (requestedForRef.current === account) return;
    requestedForRef.current = account;

    try {
      // No argument: auth-service mints for whoever the access token belongs to,
      // so the token's subject is the answer, not something we ask for.
      const identityToken = await fetchSaltclickIdentityToken();

      // A token that arrives after the account changed belongs to nobody we can
      // use it for, so it is dropped rather than handed over.
      if (accountIdRef.current !== account) return;

      // Belt and braces on the one thing that must not go wrong: the token has
      // to be for the account we think is logged in. Anything else would file
      // the conversation under someone else.
      if (identityToken.sub && identityToken.sub !== account) {
        DevLogger.error(
          `[Saltclick] identity token was minted for ${identityToken.sub}, ` +
            `not ${account} — leaving the chat session anonymous`,
        );

        return;
      }

      // Another trigger verified this account while the request was out.
      if (getSaltclickVerifiedForUserId() === account) return;

      verificationApi.setIdentityToken({
        identityTokenType: identityToken.tokenType ?? 'JWT',
        identityToken: identityToken.token,
      });
      setSaltclickVerifiedForUserId(account);
      // Logged because the failure this replaced was silent: an anonymous session
      // looks identical to a verified one from the outside, so "did the token
      // land?" was otherwise unanswerable from a support session. The token itself
      // is never logged — it is a bearer credential; `kid` and `expiresIn` identify
      // it without carrying anything sensitive.
      DevLogger.log(
        `[Saltclick] Verified chat session for ${account} (kid ${identityToken.kid ?? 'unknown'}` +
          `, expires in ${identityToken.expiresIn ?? '?'}s)`,
      );

      // Every re-verify follows a `clearSession`, and `clearSession` rebuilds the widget's
      // markup — which loses the staged pre-chat fields with it. Staging here covers all
      // three paths at once (an account handover, a token the widget says expired, and the
      // end-of-chat flow) instead of each remembering separately; two of them used to
      // forget, and a conversation that begins with no `gtAdminPanelId` has nothing on it
      // for Salesforce to key session control off.
      sendPrechatFields();
    } catch (err) {
      // Leaves the session anonymous rather than taking the whole widget down.
      // Includes the 503 an environment returns until its signing key is
      // provisioned, and the 400 for an account whose user type Salesforce has no
      // mapping for. Note this deployment runs `authMode: "Auth"`, where an
      // anonymous session cannot start a conversation at all — the launcher is
      // there but inert, so this line is the only explanation of why.
      DevLogger.error('[Saltclick] Could not mint a chat identity token: ', err);
    } finally {
      // Only clear the slot this call owns; a newer request for a different
      // account has already claimed it.
      if (requestedForRef.current === account) requestedForRef.current = null;
    }
  }, [sendPrechatFields]);

  /**
   * One treatment per ended conversation, whichever signal reaches it first.
   *
   * `isHandling` is the in-flight guard. `hasHandled` is released only when a new
   * conversation is in play, because the signals do not arrive once each: End chat
   * produces an RPC method and usually a session-status event, and the `clearSession`
   * below resets the widget, which is itself an end signal.
   */
  const endStateRef = useRef({ isHandling: false, hasHandled: false });

  /**
   * The conversation this document retired and has not seen replaced yet, and the timer
   * holding the widget inert until it is. Refs because the listeners that read them
   * outlive the render they were registered in.
   */
  const retiredConversationRef = useRef<string | undefined>(undefined);
  const staleViewTimerRef = useRef<number | undefined>(undefined);

  /** The conversation the widget is in, as last reported by anything other than an end. */
  const liveConversationRef = useRef<string | undefined>(undefined);
  /**
   * An end waiting out `REPLY_WINDOW_MS`: the conversation it was reported on, and the
   * timer that retires it. One at a time — a second signal for the same end is normal and
   * changes nothing — and dropped by a reply, which is a session going live again on that
   * conversation.
   */
  const pendingRetireRef = useRef<
    { conversationId: string | undefined; timer: number } | undefined
  >(undefined);

  /**
   * Retires the conversation the user just ended, so the next chat is a new one.
   *
   * The order is the whole design, and each step cost a round of testing:
   *
   *  1. close the conversation over scrt2 — `clearSession` cannot, it only reaches the
   *     `/session` variant, and the widget resumes any open conversation as it boots;
   *  2. `clearSession(false)` — closing server-side says nothing about what the widget
   *     still believes it holds, and there is no API for handing it that news;
   *  3. re-verify — the widget asks for a token the moment it is cleared, and the next
   *     chat cannot start on this `authMode: "Auth"` deployment without one;
   *  4. re-stage the pre-chat fields — the next conversation has not begun, which is the
   *     only window Salesforce accepts them in.
   */
  const handleChatEnded = useCallback(
    async (trigger: string, knownConversationIds: string[] = []): Promise<void> => {
      const account = accountIdRef.current;
      if (!account) return;

      // Only an end for the session this document holds. Logout ends the session too, and
      // the same clear empties the widget's storage — which the storage watcher reads as
      // an end moments later. Without this the last act of a logout would be to retire the
      // conversation the user is expected to come back to.
      if (getSaltclickVerifiedForUserId() !== account) {
        DevLogger.log(
          `[Saltclick] [${trigger}] a chat ended, but this document holds no verified ` +
            'session — leaving the conversation alone',
        );

        return;
      }

      const { orgId, deploymentName, scrt2Url } = RemoteConfigUtils.getSaltclickChatConfig() ?? {};
      if (!orgId || !deploymentName || !scrt2Url) {
        DevLogger.error(
          `[Saltclick] [${trigger}] a chat ended, but SALTCLICK_CHAT_CONFIG is ` +
            'incomplete — the conversation stays open and the next chat will resume it',
        );

        return;
      }

      const endState = endStateRef.current;
      if (endState.isHandling || endState.hasHandled) {
        // Not a warning: a second signal for the same end is normal, and saying so is how
        // you tell "already handled" apart from "never fired".
        DevLogger.log(`[Saltclick] [${trigger}] already handled for this end — not running twice`);

        return;
      }
      endState.isHandling = true;
      endState.hasHandled = true;
      DevLogger.log(`[Saltclick] [${trigger}] chat ended — retiring the conversation`);

      // Nothing reaches the widget until this settles. The deadline is what makes that
      // safe to say: an inert launcher nobody hands back is worse than the churn it is
      // here to prevent.
      document.body.classList.add(RETIRING_CLASS);
      const unblockTimer = window.setTimeout(() => {
        DevLogger.error(
          `[Saltclick] Retiring the conversation took longer than ${RETIRING_UNBLOCK_MS}ms — ` +
            'putting the launcher back before it finishes',
        );
        document.body.classList.remove(RETIRING_CLASS);
      }, RETIRING_UNBLOCK_MS);

      // Put an open window away before anything else, unless something already has.
      minimizeChatWindow();

      let retired = false;
      try {
        await closeOpenSaltclickConversations({
          scrt2Url,
          orgId,
          deploymentName,
          // Only when it is something other than the conversation being retired: a chat
          // started since this end — during the grace period below, say — is in the same
          // open list and is nobody's to take.
          keepConversationId:
            liveConversationRef.current === knownConversationIds[0]
              ? undefined
              : liveConversationRef.current,
        });
        retired = true;
        // What the widget will come back showing, and what its New Conversation button
        // would try to start a session on. Remembered so the first open after this can be
        // held inert until a live conversation replaces the view.
        retiredConversationRef.current = knownConversationIds[0] ?? 'unknown';

        // Half of making the widget let go, and the half `clearSession` does not do: it
        // decides what to resume from what it kept in web storage on this origin, and it
        // looks there before anything on this page can intervene. An id left behind now
        // points at a conversation that was deleted a moment ago, which is what had the
        // widget open, sit on its hello screen and shut itself on every click until the
        // page was reloaded. Safe here and nowhere else: everything the account had open
        // has just been closed, so an id still in storage is dead by definition.
        purgeStoredSaltclickConversation();

        // The other half, and never conditionally — this is the step that lets the user
        // have another chat at all.
        //
        // It reads like it could be skipped, and it cannot. After its own End chat the
        // widget removes its conversation id from web storage, which looks exactly like
        // letting go, and it has not: the conversation is still in the client's memory,
        // and `New Conversation` starts a new SESSION on the conversation it is holding.
        // With that conversation deleted there is nothing to start on, so the widget sits
        // on its hello screen — through a collapse, through another click, until the
        // document is replaced. Measured on dev, and the same wall the first attempt at
        // this ticket hit.
        //
        // `false`, because the conversation is gone: having the widget call its own
        // `endSession` would be a 403 on something that no longer exists.
        //
        // The cost is that `clearSession` tears the widget's markup down and rebuilds it,
        // which the user sees. That is what being minimized first and unclickable
        // throughout is for — it cannot be avoided, only kept out of the way.
        await clearSaltclickSession(false);
        // Dropping the marker is what lets `verifySession` past its already-verified
        // guard, and is true in its own right — the widget is verified as nobody now.
        setSaltclickVerifiedForUserId(undefined);
        // The token has to be back before the launcher answers again: this deployment
        // runs `authMode: "Auth"`, where an unverified widget cannot open a conversation.
        // Inside the block, therefore, not after it.
        await verifySession();
        DevLogger.log('[Saltclick] Ready for a new conversation');
      } catch (err) {
        DevLogger.error('[Saltclick] Could not retire the ended conversation: ', err);
        // Released only when nothing was taken down yet, so the next signal for this same
        // end gets a turn. Past the close there is no retry: the clear that follows it is
        // itself an end signal, so releasing the guard there would spin.
        if (!retired) endState.hasHandled = false;
      } finally {
        endState.isHandling = false;
        window.clearTimeout(unblockTimer);
        document.body.classList.remove(RETIRING_CLASS);
      }
    },
    // `verifySession` re-stages the pre-chat fields itself, which is why they are not
    // written here as well.
    [verifySession],
  );

  /**
   * Drops a retirement that is waiting out the reply window, if there is one.
   *
   * With a `conversationId`, only when it is the conversation the end was reported on: a
   * status update for a different conversation is a new chat, not a reply, and the ended
   * one still has to go. An end that arrived with no id is dropped by any reply.
   */
  const cancelPendingRetire = useCallback((reason: string, conversationId?: string): void => {
    const pending = pendingRetireRef.current;
    if (!pending) return;
    if (pending.conversationId && conversationId && pending.conversationId !== conversationId) {
      return;
    }

    window.clearTimeout(pending.timer);
    pendingRetireRef.current = undefined;
    DevLogger.log(`[Saltclick] Keeping conversation ${pending.conversationId ?? '?'} — ${reason}`);
  }, []);

  /**
   * Retires the conversation after `REPLY_WINDOW_MS`, unless a reply cancels it first.
   *
   * Every end signal comes through here rather than retiring on the spot, whoever ended
   * the chat: Salesforce leaves the conversation resumable, and the window is the chance
   * to resume it. Refused once a retirement has run for this end — the clear that follows
   * one is itself an end signal, and a timer armed by it would fire on whatever
   * conversation the user has started since.
   */
  const schedulePendingRetire = useCallback(
    (trigger: string, conversationId: string | undefined): void => {
      const endState = endStateRef.current;
      if (endState.isHandling || endState.hasHandled) {
        DevLogger.log(`[Saltclick] [${trigger}] already handled for this end — not scheduling`);

        return;
      }
      if (pendingRetireRef.current) {
        DevLogger.log(`[Saltclick] [${trigger}] an end is already waiting out the reply window`);

        return;
      }

      DevLogger.log(
        `[Saltclick] [${trigger}] chat ended — retiring conversation ${conversationId ?? '?'} ` +
          `in ${REPLY_WINDOW_MS}ms unless the user replies`,
      );
      pendingRetireRef.current = {
        conversationId,
        timer: window.setTimeout(() => {
          pendingRetireRef.current = undefined;
          void handleChatEnded(
            `${trigger}, unanswered for ${REPLY_WINDOW_MS}ms`,
            conversationId ? [conversationId] : [],
          );
        }, REPLY_WINDOW_MS),
      };
    },
    [handleChatEnded],
  );

  // A pending retirement belongs to the account that ended the chat. Left armed across a
  // handover it would fire under the next one, on a conversation that is not theirs.
  useEffect(
    () => () => {
      cancelPendingRetire('the acting account changed');
    },
    [accountId, cancelPendingRetire],
  );

  useEffect(() => {
    if (!enabled) return undefined;

    ensureVisibilityStyle();
    window.addEventListener('onEmbeddedMessagingConversationOpened', fireHelpChatbotButtonTapped);

    // A conversation being in play is the only thing that makes the next end meaningful,
    // and so the only thing that releases the guard. Both events, because either can be
    // first: a resumed conversation opens without starting.
    const onConversationInPlay = () => {
      endStateRef.current.hasHandled = false;
    };
    window.addEventListener('onEmbeddedMessagingConversationStarted', onConversationInPlay);
    window.addEventListener('onEmbeddedMessagingConversationOpened', onConversationInPlay);

    /**
     * The only public event that reports a chat ending — and only sometimes.
     *
     * `Ended` does not mean the user ended it: Agentforce ends an idle session within
     * about a minute, and typing after that starts a new session on the same conversation
     * with its history intact. Acting on those on the spot took chats away from people
     * who were still reading them, which is what the reply window is for — every `Ended`
     * counts, and none of them is acted on for `REPLY_WINDOW_MS`.
     *
     * It also leaves the gap the RPC tap exists to fill: a session Agentforce already
     * timed out is already `Ended`, so pressing End chat afterwards changes no status and
     * fires no event at all.
     */
    const onSessionStatusUpdate = (event: Event) => {
      const { detail } = event as CustomEvent<{
        conversationEntry?: { entryPayload?: string };
        conversationId?: string;
      }>;

      let payload: ISaltclickSessionStatusPayload;
      try {
        // A JSON string inside the detail, not an object. Parsed defensively: a shape
        // change should cost this trigger, not the widget.
        payload = JSON.parse(
          detail?.conversationEntry?.entryPayload ?? '{}',
        ) as ISaltclickSessionStatusPayload;
      } catch (err) {
        DevLogger.error('[Saltclick] A session status update carried an unreadable payload: ', err);

        return;
      }

      const conversationId = payload.conversationIdentifier ?? detail?.conversationId;
      // Logged before any guard runs, because this event is the only public word on a chat
      // ending: a path that returns in silence is indistinguishable from Salesforce having
      // sent nothing, and those are very different problems.
      DevLogger.log(
        `[Saltclick] Session status ${payload.sessionStatus ?? '?'} (was ` +
          `${payload.sessionStatusPrev ?? '?'}) by ${payload.sessionEndedByRole ?? 'nobody named'}` +
          ` on conversation ${conversationId ?? '?'}`,
      );

      // A conversation that is not the one we retired is the live one taking its place,
      // which is exactly what the guard was waiting for.
      if (conversationId && conversationId !== retiredConversationRef.current) {
        retiredConversationRef.current = undefined;
        releaseStaleViewGuard(`conversation ${conversationId} is in play`);
      }

      if (payload.sessionStatus !== 'Ended') {
        // Anything that is not an end says this conversation is alive. A bot handing off
        // to a live agent reports `Waiting` while it routes and `Active` once the agent
        // joins, on the same conversation, and never `Ended` (measured on dev,
        // 2026-09-01). `Inactive` is Salesforce idling a session out — an agent leaving,
        // say — and is deliberately not an end here: the conversation is still open, and
        // nothing has been decided about it.
        if (conversationId) liveConversationRef.current = conversationId;

        // A session going live on a conversation whose end is waiting out the reply window
        // is the user replying — the one thing the window exists to wait for.
        if (payload.sessionStatus === 'Active' || payload.sessionStatus === 'Waiting') {
          cancelPendingRetire(`its session is ${payload.sessionStatus} again`, conversationId);
        }

        return;
      }

      // Whoever ended it. `sessionEndedByRole` is logged above and deliberately not acted
      // on: a transfer never reports an end (see above), so every `Ended` is a chat that
      // is over — and the role vocabulary is not knowable from here, so a gate on it
      // ignores exactly the ends nobody predicted.
      //
      // Not retired on the spot, either. The conversation is resumable for as long as it
      // is open, and support asked for that window over the office on the next session —
      // `REPLY_WINDOW_MS` has the trade.
      schedulePendingRetire('onEmbeddedMessagingSessionStatusUpdate', conversationId);
    };
    window.addEventListener('onEmbeddedMessagingSessionStatusUpdate', onSessionStatusUpdate);

    /** Hands the widget back, however the wait ended. */
    const releaseStaleViewGuard = (reason: string) => {
      if (!staleViewTimerRef.current) return;

      window.clearTimeout(staleViewTimerRef.current);
      staleViewTimerRef.current = undefined;
      document.body.classList.remove(GUARDED_CLASS);
      DevLogger.log(`[Saltclick] The widget is live again — ${reason}`);
    };

    /**
     * Holds the widget inert the first time it is opened after a chat was retired.
     *
     * It comes back showing what the retired conversation left behind, and that view has a
     * **New Conversation** button which starts a session on the conversation the widget is
     * still holding — the one we deleted. Pressing it reconnects forever and takes the
     * widget with it until the page is reloaded. There is no way to disable a button on
     * Salesforce's origin, so the frame goes inert until the widget has replaced the view
     * with a live conversation, which it does on its own within a second or two.
     *
     * Nothing is lost by waiting: opening the window is what starts the next conversation,
     * and that happens without a click.
     */
    const onWindowOpened = () => {
      if (!retiredConversationRef.current || staleViewTimerRef.current) return;

      DevLogger.log(
        `[Saltclick] The widget is showing the view retired conversation ` +
          `${retiredConversationRef.current} left behind — holding it inert until a live ` +
          'conversation replaces it',
      );
      document.body.classList.add(GUARDED_CLASS);
      staleViewTimerRef.current = window.setTimeout(() => {
        DevLogger.error(
          `[Saltclick] No new conversation within ${STALE_VIEW_GUARD_MS}ms — handing the ` +
            'widget back with the retired view still showing',
        );
        // Deliberately not cleared: the view is still the stale one, so the next open
        // should guard again.
        releaseStaleViewGuard('the wait timed out');
      }, STALE_VIEW_GUARD_MS);
    };
    window.addEventListener('onEmbeddedMessagingWindowMaximized', onWindowOpened);
    window.addEventListener('onEmbeddedMessagingConversationOpened', onWindowOpened);

    // Two names that read like the answer and are not: `onEmbeddedMessagingEndSession` is
    // an internal iframe event that never reaches the host, and
    // `onEmbeddedMessagingConversationClosed` has never been seen to fire on this
    // deployment. Registered anyway — they cost two listeners, they are the documented
    // way this is supposed to work, and the guard makes an extra signal free.
    const onEndSession = () => {
      schedulePendingRetire('onEmbeddedMessagingEndSession', liveConversationRef.current);
    };
    window.addEventListener('onEmbeddedMessagingEndSession', onEndSession);
    const onConversationClosed = () => {
      schedulePendingRetire('onEmbeddedMessagingConversationClosed', liveConversationRef.current);
    };
    window.addEventListener('onEmbeddedMessagingConversationClosed', onConversationClosed);

    // Before the bootstrap script is appended below, and there is no second chance at it:
    // the RPC tap has to be in place before the widget builds its channel.
    const removeEndSignals = installSaltclickEndSignals({
      // The nets carry no conversation id. The last one reported live is the one that
      // ended, and naming it is what lets the retirement spare a chat started since.
      onChatEnded: (trigger) => {
        schedulePendingRetire(trigger, liveConversationRef.current);
      },
      // The second way out of the one-per-end guard. `onEmbeddedMessagingConversationStarted`
      // is the first, and it is a Salesforce event on a deployment where most of them never
      // arrive — if it is one of those, the guard latches after the first end and nothing
      // ends again for the rest of the page load.
      onConversationSeen: onConversationInPlay,
    });

    // The class swaps the skeleton for the real launcher (or drops it when the
    // widget never arrives). It lives on the body, not in state, so it survives
    // the hook remounting on a fresh login.
    const hideSkeleton = () => document.body.classList.add(READY_CLASS);

    /**
     * What is known about the widget right now, for the lines below.
     *
     * Each of these is a different reason for an empty corner, and they are
     * indistinguishable from the outside: the script never loaded, it loaded but
     * `init` was never reached, `init` ran but the widget never said it was ready, or
     * everything worked and our own CSS is hiding it.
     */
    const widgetState = () =>
      [
        `script ${document.getElementById(SCRIPT_ID) ? 'present' : 'missing'}`,
        `bootstrap ${window.embeddedservice_bootstrap ? 'present' : 'missing'}`,
        `container ${document.getElementById('embedded-messaging') ? 'present' : 'missing'}`,
        `body classes ${document.body.className || '(none)'}`,
      ].join(', ');

    /**
     * Reports a widget that never rendered anything.
     *
     * Ready is not the same as visible: the event says the widget booted, not that it
     * painted a launcher. A ready widget with an empty container is the "chat is just
     * not there" report, and until now it produced no log of any kind — the skeleton
     * timed out, the corner went empty, and nothing said why.
     */
    let paintCheckTimer: number | undefined;
    const checkWidgetPainted = () => {
      const container = document.getElementById('embedded-messaging');
      if (container?.childElementCount) return;

      DevLogger.error(`[Saltclick] Widget reported ready but rendered nothing — ${widgetState()}`);
    };

    const onReady = () => {
      // Or the deadline below fires twenty seconds into every session and reports a widget
      // that has been ready the whole time — which is worse than no diagnostic, because it
      // is the line someone reads while looking for a real failure.
      window.clearTimeout(skeletonTimer);
      hideSkeleton();
      setIsMessagingReady(true);
      DevLogger.log('[Saltclick] Widget ready');
      // Late enough for the launcher to have painted, since ready fires before it.
      paintCheckTimer = window.setTimeout(checkWidgetPainted, PAINT_CHECK_DELAY_MS);
      // `userVerificationAPI` only exists when User Verification is enabled on the
      // Salesforce deployment. Without it there is nothing to hand a token to, so
      // say so once — otherwise the session is quietly anonymous and the missing
      // token request looks like a bug on this side.
      if (!window.embeddedservice_bootstrap?.userVerificationAPI) {
        DevLogger.error(
          '[Saltclick] Widget is ready but exposes no userVerificationAPI — User ' +
            'Verification is not enabled on this Salesforce deployment, so the chat ' +
            'session stays anonymous',
        );
      }
    };
    window.addEventListener('onEmbeddedMessagingReady', onReady);
    // The deadline used to drop the skeleton in silence, which is the worst version of
    // this failure: the corner simply goes empty, with nothing in the console to say
    // the widget never arrived or how far it got.
    const skeletonTimer = window.setTimeout(() => {
      hideSkeleton();
      DevLogger.error(
        `[Saltclick] Widget never became ready in ${SKELETON_TIMEOUT_MS}ms — ${widgetState()}`,
      );
    }, SKELETON_TIMEOUT_MS);

    // The widget asks for a token whenever the one it holds stops being usable —
    // expiry, or the reset that follows clearSession.
    const onIdentityTokenExpired = () => {
      // Dropping the marker is what lets `verifySession` past its already-verified
      // guard, and is true in its own right: the session stops counting as verified
      // until a fresh token is handed over.
      setSaltclickVerifiedForUserId(undefined);
      void verifySession();
    };
    window.addEventListener('onEmbeddedMessagingIdentityTokenExpired', onIdentityTokenExpired);

    let cancelled = false;
    // Remote config is fetched fire-and-forget at startup, so on a first login
    // the values aren't activated yet — wait for them instead of bailing out.
    void remoteConfigReady.then(() => {
      if (cancelled || document.getElementById(SCRIPT_ID)) return;

      // Same SALTCLICK_CHAT_CONFIG remote-config key as mobile; fails closed
      // (no bundled default) when unset or incomplete.
      const { orgId, deploymentName, siteUrl, scrt2Url } =
        RemoteConfigUtils.getSaltclickChatConfig() ?? {};
      if (!orgId || !deploymentName || !siteUrl || !scrt2Url) {
        DevLogger.error('[Saltclick] Missing or incomplete SALTCLICK_CHAT_CONFIG remote config');
        hideSkeleton();
        return;
      }

      const script = document.createElement('script');
      script.id = SCRIPT_ID;
      script.src = `${siteUrl}/assets/js/bootstrap.min.js`;
      script.onerror = () => {
        DevLogger.error('[Saltclick] Failed to load bootstrap script');
        hideSkeleton();
      };
      script.onload = () => {
        const bootstrap = window.embeddedservice_bootstrap;
        if (!bootstrap) {
          DevLogger.error('[Saltclick] embeddedservice_bootstrap not available after script load');
          hideSkeleton();
          return;
        }
        try {
          bootstrap.settings.language = 'en_US';
          // Otherwise "the script loaded but init was never reached" and "init ran and
          // the widget never appeared" look identical from a console.
          DevLogger.log(`[Saltclick] Calling init for ${deploymentName} @ ${orgId}`);
          bootstrap.init(orgId, deploymentName, siteUrl, { scrt2URL: scrt2Url });
        } catch (err) {
          DevLogger.error('Error loading Embedded Messaging: ', err);
          hideSkeleton();
        }
      };
      document.body.appendChild(script);
    });

    return () => {
      cancelled = true;
      window.clearTimeout(skeletonTimer);
      window.clearTimeout(paintCheckTimer);
      removeEndSignals();
      window.removeEventListener(
        'onEmbeddedMessagingConversationOpened',
        fireHelpChatbotButtonTapped,
      );
      window.removeEventListener('onEmbeddedMessagingConversationStarted', onConversationInPlay);
      window.removeEventListener('onEmbeddedMessagingConversationOpened', onConversationInPlay);
      cancelPendingRetire('the widget is going away');
      releaseStaleViewGuard('the widget is going away');
      document.body.classList.remove(GUARDED_CLASS);
      window.removeEventListener('onEmbeddedMessagingWindowMaximized', onWindowOpened);
      window.removeEventListener('onEmbeddedMessagingConversationOpened', onWindowOpened);
      window.removeEventListener('onEmbeddedMessagingSessionStatusUpdate', onSessionStatusUpdate);
      window.removeEventListener('onEmbeddedMessagingEndSession', onEndSession);
      window.removeEventListener('onEmbeddedMessagingConversationClosed', onConversationClosed);
      window.removeEventListener('onEmbeddedMessagingReady', onReady);
      window.removeEventListener('onEmbeddedMessagingIdentityTokenExpired', onIdentityTokenExpired);
    };
    // `handleChatEnded` is stable, and it is what every end signal this effect registers
    // ends up in, so it belongs here rather than being silenced.
  }, [cancelPendingRetire, enabled, schedulePendingRetire, verifySession]);

  // Verified identity. `userVerificationAPI` only exists once messaging is ready,
  // so it is the real gate — `isMessagingReady` is just the signal to re-run.
  // Keyed on the account rather than the acting office: switching between one's
  // own offices is the same person and must not restart the conversation, while a
  // different login in the same tab must. Also covers a fresh login where the
  // already-init'd widget never fires the ready event again.
  useEffect(() => {
    if (!enabled || !accountId) return;

    if (getSaltclickVerifiedForUserId() === accountId) return;

    const verificationApi = window.embeddedservice_bootstrap?.userVerificationAPI;
    if (verificationApi && getSaltclickVerifiedForUserId()) {
      // A session verified as someone else has to be ended before this account
      // can take over — the fallback for a handover logout did not get to clean
      // up after (a crashed tab, or a session dropped without LOGOUT_ACTION).
      void endSaltclickChatSession().then(verifySession);

      return;
    }

    void verifySession();
  }, [accountId, enabled, isMessagingReady, verifySession]);

  // Re-stages on a fresh login in the same tab, and once the widget is ready to accept
  // them. No longer on an office switch: the fields now describe the account, which a
  // switch does not change.
  useEffect(() => {
    if (!accountId) return;

    sendPrechatFields();
  }, [accountId, isMessagingReady, sendPrechatFields]);

  /**
   * Catches a conversation that ended with nobody telling this page.
   *
   * The four end nets all watch the widget, which means they cover every end the widget
   * hears about and none that it does not: an agent closing a conversation from their
   * console while this tab was on another route fires nothing here. The widget resumes
   * from web storage regardless, so the next open lands on the view of a conversation
   * that no longer exists — with a **New Conversation** button that starts a session on
   * it and never connects.
   *
   * So whenever Help comes up holding a conversation, Salesforce is asked whether that
   * conversation is actually open. Gone means the same repair an end gets, run before the
   * user clicks rather than after: the launcher is a placeholder for a moment instead of
   * a chat that opens into a dead end.
   *
   * Only while storage names one, and once per id: with nothing held there is nothing
   * that can be stale, and the great majority of visits to Help have never opened a chat
   * — three round trips each would be the whole cost of this check with none of its
   * value. A failed read asks again next time and takes nothing away.
   */
  const checkedConversationsRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (!enabled || !isHelpRoute || !isMessagingReady) return;

    const heldIds = getStoredSaltclickConversationIds();
    if (!heldIds.length) return;

    const held = heldIds.join(',');
    if (checkedConversationsRef.current === held) return;
    checkedConversationsRef.current = held;

    const { orgId, deploymentName, scrt2Url } = RemoteConfigUtils.getSaltclickChatConfig() ?? {};
    if (!orgId || !deploymentName || !scrt2Url) return;

    void listOpenSaltclickConversationIds({ orgId, deploymentName, scrt2Url })
      .then((openIds) => {
        // `some`, not `every`: one conversation still open is one the widget can resume,
        // which is the case that must be left alone.
        if (heldIds.some((heldId) => openIds.includes(heldId))) {
          DevLogger.log(
            `[Saltclick] The conversation the widget holds (${held}) is still listed open — ` +
              'nothing to repair',
          );

          return;
        }

        DevLogger.log(
          `[Saltclick] The widget is holding ${held}, which Salesforce no longer lists open — ` +
            'clearing it before the user opens a dead conversation',
        );

        void handleChatEnded('help route: the held conversation is gone', heldIds);
      })
      .catch((err) => {
        // Asked again on the next visit: not knowing must never cost somebody a live
        // chat, and this runs speculatively rather than on an end.
        checkedConversationsRef.current = undefined;
        DevLogger.error('[Saltclick] Could not check whether the held conversation is open: ', err);
      });
  }, [enabled, handleChatEnded, isHelpRoute, isMessagingReady]);

  useEffect(() => {
    if (!isHelpRoute) return undefined;

    if (!enabled) {
      // Standing on Help with the widget hidden, which is correct on the way out of a
      // session and wrong at any other time. Worth a line because it looks exactly like
      // a broken widget: the launcher is not there and nothing else explains it. The
      // widget is init'd once per document and cannot be re-created, so if `enabled`
      // went false transiently — a user record briefly absent from redux, say — the
      // corner stays empty even though everything loaded.
      DevLogger.log(
        '[Saltclick] On Help but the widget is disabled ' +
          `(flag ${flagEnabled ? 'on' : 'off'}, user ${user ? 'present' : 'missing'}) — hiding it`,
      );

      return undefined;
    }

    document.body.classList.add(VISIBLE_CLASS);

    return () => document.body.classList.remove(VISIBLE_CLASS);
  }, [enabled, flagEnabled, isHelpRoute, user]);
};
